import React, { useCallback, useState } from 'react';
import { setVMTDataFromTransportation } from '../../services/vmtDataService';
import { useLocation } from '../../contexts/LocationContext';
import { ChartLayout, EquityContent, MethodologyContent, OverviewContent, RelevanceContent } from '../charts/ChartLayout';
import EVChargingInfrastructure from '../charts/EVChargingInfrastructure';
import EVFleetAdoption from '../charts/EVFleetAdoption';
import { KeyMetric, KeyMetrics } from '../charts/KeyMetrics';
import { ModeShareData, TransportationModeShare } from '../charts/TransportationModeShare';
import { VMTChart, VMTMetrics } from '../charts/VMTChart';
import { VMTComparisonChart } from '../charts/VMTComparisonChart';
import { PageHeader } from '../layout/PageHeader';
import EVAdoptionBarriersChart from '../charts/EVAdoptionBarriersChart';
import MeansOfTransportation from '../charts/MeansOfTransportation';


interface TransportationProps {
  location?: string;
}

export const Transportation: React.FC<TransportationProps> = ({ location: propLocation }) => {
  const { selectedLocation, selectedGeofips, coreDataLoading } = useLocation();
  const [activeVMTView, setActiveVMTView] = useState<string>('overview');
  const [activeEVView, setActiveEVView] = useState<string>('overview');
  const [activeModeShareView, setActiveModeShareView] = useState<string>('overview');
  const [activeChargingView, setActiveChargingView] = useState<string>('overview');
  const [vmtChartView, setVmtChartView] = useState<string>('total');
  const [vmtMetrics, setVmtMetrics] = useState<VMTMetrics | null>(null);
  // VMT per capita metrics removed - no longer needed
  const [modeShareData, setModeShareData] = useState<ModeShareData | null>(null);
  const [evOverview, setEvOverview] = useState<{ observedYear: number; observedTotal: number; projected2030: number; target2030: number; onPace: boolean } | null>(null);
  const [evChargingView, setEvChargingView] = useState<'counts' | 'peoplePerCharger' | 'evsPerCharger'>('counts');
  const [evAdoptionView, setEvAdoptionView] = useState<'total' | 'perCapita' | 'projection'>('projection');
  const [vmtDataTableContent, setVmtDataTableContent] = useState<React.ReactNode>(null);
  const [vmtShowDataTable, setVmtShowDataTable] = useState(false);

  const currentLocation = propLocation || selectedLocation;

  const toggleVMTView = (view: string) => {
    setActiveVMTView(view);
  };

  const toggleEVView = (view: string) => {
    setActiveEVView(view);
  };




  const handleVMTMetricsCalculated = useCallback((metrics: VMTMetrics) => {
    setVmtMetrics(metrics);
  }, []);

  // VMT per capita metrics handler removed - no longer needed

  const handleVMTDataCalculated = useCallback((data: any[]) => {
    console.log('🚗 Transportation page received VMT data:', data);
    console.log('🚗 VMT data length:', data.length);
    console.log('🚗 VMT data years:', data.map(d => d.year));
    
    // Set VMT data using the new service for GHG inventory
    setVMTDataFromTransportation(data);
    
    // Also set legacy window variable for backward compatibility
    if (typeof window !== 'undefined') {
      window.vmtChartData = data;
      console.log('🚗 VMT data set for GHG inventory (legacy):', data);
    }
  }, []);

  const handleModeShareCalculated = useCallback((modeShare: ModeShareData) => {
    setModeShareData(modeShare);
  }, []);

  if (!currentLocation) {
    return (
      <div>
        <PageHeader 
          title="Transportation & Mobility" 
          subtitle="Please select a location to view transportation data."
        />
      </div>
    );
  }

  if (coreDataLoading) {
    return (
      <div>
        <PageHeader 
          title="Loading core data..." 
          subtitle="Preloading VMT, electricity, and natural gas data for this location."
          variant="transportation"
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader 
        title={`Transportation & Mobility - ${currentLocation}`}
        subtitle="Comprehensive transportation insights and mobility trends for the selected location."
        variant="transportation"
      />

      {/* Vehicle Miles Traveled Section */}
      <ChartLayout
        title="Vehicle Miles Traveled (VMT)"
        chartType="bar"
        keyMetrics={
          <KeyMetrics>
            <KeyMetric 
              value={vmtMetrics ? `${vmtMetrics.currentVMT}` : "Loading..."} 
              label="Average daily VMT per capita (2023)"
              color="#1e3a8a"
            />
            <KeyMetric 
              value={vmtMetrics ? `${vmtMetrics.changeSince2005 > 0 ? '+' : ''}${vmtMetrics.changeSince2005}%` : "Loading..."} 
              label="Change since 2005"
              color={(vmtMetrics ? vmtMetrics.changeSince2005 : 0) < 0 ? "#10b981" : "#ef4444"}
            />
            <KeyMetric 
              value={vmtMetrics ? `${vmtMetrics.californiaAverage}` : "Loading..."} 
              label="California average VMT"
              color="#f59e0b"
            />
          </KeyMetrics>
        }
        chartContent={
          vmtChartView === 'comparison' ? (
            <VMTComparisonChart location={currentLocation} geofips={selectedGeofips} />
          ) : (
            <VMTChart 
              location={currentLocation} 
              geofips={selectedGeofips || null} 
              chartView={vmtChartView as 'total' | 'percapita'} 
              onMetricsCalculated={handleVMTMetricsCalculated} 
              onDataCalculated={handleVMTDataCalculated}
              onDataTableReady={(content, toggleFn) => {
                setVmtDataTableContent(content);
                // Store toggle function in a ref or state if needed
              }}
            />
          )
        }
        dataTableContent={vmtChartView !== 'comparison' ? vmtDataTableContent : undefined}
        showDataTable={vmtChartView !== 'comparison' ? vmtShowDataTable : false}
        onToggleDataTable={() => setVmtShowDataTable(!vmtShowDataTable)}
        chartSelector={{
          options: [
            { value: 'total', label: 'Total VMT' },
            { value: 'percapita', label: 'Per Capita' },
            { value: 'comparison', label: 'VMT Comparison' }
          ],
          value: vmtChartView,
          onChange: setVmtChartView
        }}
        overviewContent={
          vmtChartView === 'comparison' ? (
            <OverviewContent
              title="Light-Duty vs Heavy-Duty VMT Comparison"
              description="This chart compares light-duty and heavy-duty vehicle miles traveled, highlighting the different trends and contributions of each vehicle class."
            >
              <p>
                This comparison reveals the relative contributions of light-duty passenger vehicles versus heavy-duty commercial vehicles to total VMT in {currentLocation}. Understanding this distribution is important for targeting emission reduction strategies, as heavy-duty vehicles typically have higher emissions per mile but lower total miles traveled.
              </p>
            </OverviewContent>
          ) : (
            <OverviewContent
              title="VMT Overview"
              description="Vehicle Miles Traveled (VMT) captures the total distance traveled by all vehicles in a community over a given period of time,
              serving as a key measure of driving activity. Statewide, VMT increased slightly year over year before declining during the COVID-19 pandemic
              as a result of stay-at-home orders and transitions to remote work. Since then, VMT has been rising at a steady pace, although not quite reaching
              peak levels prior to the pandemic."
            >
              <p>
                {vmtMetrics ? (
                  <>
                    Light-Duty vehicle miles traveled (VMT) in {currentLocation} totalled {(vmtMetrics.totalVMT2023 / 1000000).toFixed(2)} million miles in 2023 according to Replica's statewide origin-destination model. This represents a {vmtMetrics.changeSince2005 > 0 ? 'increase' : 'decrease'} of {Math.abs(vmtMetrics.changeSince2005)}% since 2005, based on changes in local traffic counts, vehicle registrations and population. 
                    {vmtMetrics.changeSince2005 < 0 ? 'This reduction reflects successful transportation demand management strategies and shifts toward more sustainable mobility options. ' : 'This increase indicates growing transportation demand that may require additional climate action measures. '} 
                    The current VMT is {vmtMetrics.currentVMT > vmtMetrics.californiaAverage ? 'above' : 'below'} the California average of {vmtMetrics.californiaAverage} miles per capita, 
                    {vmtMetrics.currentVMT > vmtMetrics.californiaAverage ? 'indicating opportunities for further improvement.' : 'showing positive performance compared to the state average.'}
                  </>
                ) : (
                  `Loading VMT data for ${currentLocation}...`
                )}
              </p>
            </OverviewContent>
          )
        }
        relevanceContent={
            <RelevanceContent
              title="Policy Relevance"
              description="Vehicle miles traveled (VMT) are the primary activity indicator for the transportation sector. Light-duty motor vehicles contribute nearly 40% of statewide emissions."
            >
              <div className="methodology-details-simple">
                <h5>Climate Action Planning</h5>
                <p>Local governments seek to limit VMT through housing densification, land use policies, public transit, biking infrastructure, micro mobility and other means. Despite these efforts, total VMT and VMT per capita continue to rise statewide.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Quality of Life Indicators</h5>
                <p>Reduced VMT may indicate that people are able to access their daily needs without traveling as far, or by active mobility or transit options, which are indicators of better quality of life.</p>
              </div>
            </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description={"Younger and low-income residents are less likely to own motor vehicles and contribute less to vehicle miles traveled, on average.\n              However, many low-income communities exist in close proximity to highways and major roads, disproportionately burdening them with the environmental\n              and health impacts of motor vehicle travel. "}
          >
            <div className="methodology-details-simple">
              <h5>Access and Distance Challenges</h5>
              <p> Higher housing prices in urban centers relative to suburban or exurban areas leads many working adults to travel long distances to reach workplaces,
                shopping, and other destinations. This often disproportionately burdens low-income communities with longer motor vehicle travel times.
                Housing affordability and accessibility, especially in high demand, job-rich areas, is a key factor in reducing VMT.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Community Design Impact</h5>
              <p>Higher VMT can also indicate a lack of amenities nearby. Designing mixed-use communities with jobs, schools, shopping, and recreation nearby
                 can reduce VMT by shifting vehicle use to shorter, local trips or encourage active transportation options like walking and biking.</p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          vmtChartView === 'comparison' ? (
            <MethodologyContent
              title="Methodology"
              description="VMT Comparison chart shows light-duty and heavy-duty vehicle miles traveled separately, using city-specific data from emfac_final_estimation for recent years and EMFAC backcasting for historical data."
              dataSources={[
                'emfac_final_estimation (city-specific EMFAC data)',
                'emfac_county_filtered (county-level backcasting ratios)',
                'VMT chart data (for LDV consistency)'
              ]}
              methodology={[
                'Light-Duty Vehicle (LDV) VMT: Uses the same calculation as the main VMT chart, including all backcasting logic, to ensure consistency across the application.',
                'Heavy-Duty Vehicle (HDV) VMT for 2015-2023: Sums all total_vmt values from emfac_final_estimation where class=HDV for the selected location (city geofips), providing city-specific HDV data.',
                'Heavy-Duty Vehicle (HDV) VMT for 2005-2014: Uses county-level backcasting ratios from emfac_county_filtered, where the 2015 HDV VMT baseline is scaled by pre-calculated backcasting_ratio values for each year.',
                'HDV backcasting applies the county ratio to city data, ensuring historical trends match broader regional patterns while maintaining city-specific values for the baseline year (2015).',
                'This approach captures the fact that HDV VMT data is only available at city-level from 2015 onwards in emfac_final_estimation, requiring backcasting for earlier years.'
              ]}
            />
          ) : (
            <MethodologyContent
              title="Methodology"
              description="VMT chart combines multiple data sources to provide a comprehensive view of vehicle miles traveled from 2005-2023, using EMFAC backcasting for historical data, AADT scaling for recent years, and Replica simulation for current data."
              dataSources={[
                'EMFAC2021',
                'CARB Fleet Database',
                'CalTrans',
                'Replica',
                'US Census (Population)'
              ]}
              methodology={[
                'Backcasting from 2005 to 2014 considers the change in county-level VMT using the California Air Resource\'s Board\'s EMFAC2021 model converted to a scaling factor.',
                'Backcasting from 2015 to 2022 considers changes vehicle ownership from the CARB Fleet Database, in addition to changes in local traffic counts, Average Annual Daily Traffic (AADT) from CalTrans.',
                'The CARB Fleet database is comprehensive of every vehicle registered in the location, providing a comprehensive measurement of vehicle ownership.',
                'CalTrans data are filtered for only monitors with complete data covering the years 2015 to 2022, providing a consistent measurement of local traffic counts.',
                'Together, these data sets measure local changes in inbound, outbound and local traffic. This measurement approach more accurately captures real world changes in local VMT than modeled approaches, which do not reliably provide accurate and consistent data over time.',
                'VMT for 2023 is from Replica\'s origin-destination travel demand model.'
              ]}
            />
          )
        }
        activeView={activeVMTView}
        onViewChange={toggleVMTView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
      />

      {/* Transportation Mode Share Section */}
      <ChartLayout
        title="Transportation Mode Share"
        chartType="bar"
        keyMetrics={
          <KeyMetrics>
            <KeyMetric 
              value={modeShareData ? `${modeShareData.privateAuto.toFixed(1)}%` : "Loading..."} 
              label="Private auto share"
              color="#dc2626"
            />
            <KeyMetric 
              value={modeShareData ? `${modeShareData.publicTransit.toFixed(1)}%` : "Loading..."} 
              label="Public transit share"
              color="#1e3a8a"
            />
            <KeyMetric 
              value={modeShareData ? `${modeShareData.walking.toFixed(1)}%` : "Loading..."} 
              label="Walking share"
              color="#10b981"
            />
            <KeyMetric 
              value={modeShareData ? `${modeShareData.biking.toFixed(1)}%` : "Loading..."} 
              label="Biking share"
              color="#059669"
            />
          </KeyMetrics>
        }
        chartContent={
          <TransportationModeShare 
            location={currentLocation} 
            geofips={selectedGeofips} 
            onModeShareCalculated={handleModeShareCalculated}
            embedOnly
          />
        }
        overviewContent={
          <OverviewContent
            title="Mode Share Overview"
            description="Transportation mode share indicates a community's transportation choices based on trip needs and infrastructure availability.
            Private automobile use continues to dominate as the primary means of transportation for Californians, with bike, pedestrian, and transit use
            varying more by location."
          >
            <p>
              {modeShareData ? (
                <>
                  {currentLocation} shows a diverse transportation mode share with {modeShareData.privateAuto.toFixed(1)}% using private automobiles, {modeShareData.publicTransit.toFixed(1)}% using public transit, {modeShareData.walking.toFixed(1)}% walking, {modeShareData.biking.toFixed(1)}% biking, {modeShareData.carpool.toFixed(1)}% carpooling, and {modeShareData.onDemand.toFixed(1)}% using on-demand services. 
                  {modeShareData.publicTransit > 20 ? ' The high transit ridership reflects strong public transportation infrastructure,' : ' The transit ridership indicates opportunities for improved public transportation,'} 
                  while the {modeShareData.walking > 5 || modeShareData.biking > 5 ? 'substantial' : 'modest'} active transportation share ({modeShareData.walking.toFixed(1)}% walking, {modeShareData.biking.toFixed(1)}% biking) indicates {modeShareData.walking > 5 || modeShareData.biking > 5 ? 'significant' : 'potential for'} active transportation investments. 
                  {modeShareData.privateAuto > 60 ? ' However, the majority still drive alone, presenting opportunities for further mode shift through improved transit service and active transportation infrastructure.' : ' The relatively balanced mode share demonstrates successful transportation demand management strategies.'}
                </>
              ) : (
                `Loading mode share data for ${currentLocation}...`
              )}
            </p>
          </OverviewContent>
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance to Climate Action"
            description="Mode share directly impacts transportation emissions and indicates the effectiveness of sustainable transportation investments."
          >
            <div className="methodology-details-simple">
              <h5>Emissions Impact</h5>
              <p>Shifting from single-occupancy vehicles to transit, walking, and cycling significantly reduces per-trip emissions.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Infrastructure Planning</h5>
              <p>Mode share data guides investments in transit service, bike lanes, pedestrian infrastructure, and carpool programs.</p>
            </div>
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description="Transportation mode access varies by income, location, and ability, requiring equitable infrastructure investment."
          >
            <div className="methodology-details-simple">
              <h5>Access Barriers</h5>
              <p>Low-income communities and communities of color often face limited access to reliable transit and safe walking/cycling infrastructure.
                Pedestrian, transit, and bicycle infrastructure is often concentrated in wealthier communities, limiting access for lower-income residents
                who would benefit from utilizing these modes.
              </p>
            </div>
            <div className="methodology-details-simple">
              <h5>Mobility Justice</h5>
              <p>All communities should have access to affordable, reliable transportation options regardless of income or location. Mode choice is critical
                to those who are unable to drive, including children, elderly, and disabled residents.
              </p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Methodology"
            description="Mode share data comes from Replica's 2023 travel simulation model, which uses comprehensive data sources to estimate transportation patterns."
            dataSources={[
              'ReplicaHQ® travel demand model (2023)',
              'Mobile location data and ground truth traffic counts',
              'Resident data and building environment information',
              'Economic activity data and transit ridership counts'
            ]}
            methodology={[
              'Mode share calculated as percentage of total VMT by transportation mode',
              'Data represents all trip purposes and daily travel patterns',
              'VMT values used to determine mode distribution across all travel',
              'Primary mode assigned to each trip segment using ranking system',
              'OTHER_TRAVEL_MODE includes micro-mobility (e-scooters, e-bikes), hybrid trips, and unmodeled travel methods',
              'Results represent comprehensive travel behavior simulation'
            ]}
          />
        }
        activeView={activeModeShareView}
        onViewChange={setActiveModeShareView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        onDownloadPNG={() => {
          // Access the chart canvas through the embedded component
          const canvas = document.querySelector('[id^="mode-share-chart"] canvas') as HTMLCanvasElement;
          if (canvas) {
            const link = document.createElement('a');
            link.download = `${currentLocation.replace(/\s+/g, '_')}_Transportation_Mode_Share.png`;
            link.href = canvas.toDataURL();
            link.click();
          }
        }}
        onDownloadCSV={() => {
          // CSV download with calculated VMT per mode
          if (modeShareData) {
            const calculateVMT = (percentage: number) => {
              return Math.round((percentage / 100) * modeShareData.totalVMT);
            };
            
            const csvData = [
              ['Mode', 'Percentage', 'VMT'],
              ['Private Auto', `${modeShareData.privateAuto.toFixed(1)}%`, calculateVMT(modeShareData.privateAuto).toLocaleString()],
              ['Public Transit', `${modeShareData.publicTransit.toFixed(1)}%`, calculateVMT(modeShareData.publicTransit).toLocaleString()],
              ['Walking', `${modeShareData.walking.toFixed(1)}%`, calculateVMT(modeShareData.walking).toLocaleString()],
              ['Biking', `${modeShareData.biking.toFixed(1)}%`, calculateVMT(modeShareData.biking).toLocaleString()],
              ['Carpool', `${modeShareData.carpool.toFixed(1)}%`, calculateVMT(modeShareData.carpool).toLocaleString()],
              ['On-Demand', `${modeShareData.onDemand.toFixed(1)}%`, calculateVMT(modeShareData.onDemand).toLocaleString()],
              ['Commercial', `${modeShareData.commercial.toFixed(1)}%`, calculateVMT(modeShareData.commercial).toLocaleString()],
              ['Other', `${modeShareData.other.toFixed(1)}%`, calculateVMT(modeShareData.other).toLocaleString()],
              ['Total', '100.0%', modeShareData.totalVMT.toLocaleString()]
            ];
            const csvContent = csvData.map(row => row.join(',')).join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `${currentLocation.replace(/\s+/g, '_')}_Transportation_Mode_Share_Data.csv`;
            link.click();
          }
        }}
        pngFilename={`${currentLocation.replace(/\s+/g, '_')}_Transportation_Mode_Share`}
        csvFilename={`${currentLocation.replace(/\s+/g, '_')}_Transportation_Mode_Share_Data`}
      />

      {/* Electric Vehicle Adoption Section */}
      <ChartLayout
        title="Electric Vehicle Adoption"
        chartType="line"
        chartSelector={{
          options: [
            { value: 'projection', label: 'Projection to 2030' },
            { value: 'total', label: 'Total (stacked)' },
            { value: 'perCapita', label: 'Per 1,000 residents' }
          ],
          value: evAdoptionView,
          onChange: (value: string) => setEvAdoptionView(value as 'total' | 'perCapita' | 'projection')
        }}
        keyMetrics={
          <KeyMetrics>
            <KeyMetric 
              value="8.7" 
              label="EVs per 1,000 residents"
              color="#1e3a8a"
            />
            <KeyMetric 
              value="+156%" 
              label="Growth since 2020"
              color="#10b981"
            />
            <KeyMetric 
              value="127" 
              label="Public charging stations"
              color="#f59e0b"
            />
          </KeyMetrics>
        }
        chartContent={
          <div id="ev-adoption-chart">
            <EVFleetAdoption 
              location={currentLocation} 
              geofips={selectedGeofips} 
              activeTab={evAdoptionView}
              onTabChange={setEvAdoptionView}
              onMetricsCalculated={setEvOverview} 
            />
          </div>
        }
        overviewContent={
          <OverviewContent
            title="EV Adoption Overview"
            description="This chart displays electric vehicle adoption trends, a critical indicator of transportation electrification progress."
          >
            <p>
              {evOverview ? (
                <>
                  In {evOverview.observedYear}, {currentLocation} had {evOverview.observedTotal.toLocaleString()} light-duty electric vehicles and is on pace to reach {Math.round(evOverview.projected2030).toLocaleString()} by 2030. To meet California's target of 5 million electric vehicles by 2030, {currentLocation} would need approximately {Math.round(evOverview.target2030).toLocaleString()} vehicles. {currentLocation} {evOverview.onPace ? 'will surpass this target if growth continues at the current rate.' : 'is not yet on pace and will need to accelerate adoption to meet the target.'}
                </>
              ) : (
                `Loading EV adoption overview for ${currentLocation}...`
              )}
            </p>
          </OverviewContent>
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance to Climate Action"
            description="Electric vehicle adoption is essential for reducing transportation emissions and achieving climate goals."
          >
            <div className="methodology-details-simple">
              <h5>Emissions Reduction</h5>
              <p>EVs produce significantly fewer emissions than gasoline vehicles, especially when powered by renewable electricity.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Infrastructure Planning</h5>
              <p>EV adoption rates guide charging infrastructure investment and grid capacity planning for transportation electrification.</p>
            </div>
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description="Access to EVs remains uneven across income, race, and geography. Equity-centered programs are necessary to ensure all Californians benefit."
          >
            <div className="methodology-details-simple">
              <h5>Affordability & Tenure</h5>
              <p>Lower-income households and renters face high upfront costs and limited home charging. Used-EV rebates and low‑income financing expand access.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Charging for Renters</h5>
              <p>Mandates and incentives for multifamily and renter access to charging reduce inequities in home charging availability.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Targeted Community Grants</h5>
              <p>Place‑based grants and community charging projects support adoption in underserved neighborhoods.</p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Methodology"
            description="Data source and roll‑up approach for EV adoption metrics."
            dataSources={[
              'EMFAC Fleet Database (CARB) built from DMV vehicle registrations',
              'Population data for per‑capita calculations'
            ]}
            methodology={[
              'We query EV fleet (fuel = Electric and PluginHybrid, class = LDV) at tract level rolled up to cities/counties using a population‑weighted correspondence table.',
              'Per‑capita chart shows EVs per 1,000 residents; statewide (geofips=6) shown for comparison.',
              'Projections, where shown, consider the 5‑year average annual growth in EV registrations.'
            ]}
          />
        }
        activeView={activeEVView}
        onViewChange={toggleEVView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        onDownloadPNG={() => {
          const canvas = document.querySelector('#ev-adoption-chart canvas') as HTMLCanvasElement;
          if (canvas) {
            const link = document.createElement('a');
            const viewLabel = evAdoptionView === 'total' ? 'Total' : evAdoptionView === 'perCapita' ? 'Per_Capita' : 'Projection';
            link.download = `EV_Adoption_${viewLabel}_${currentLocation.replace(/\s+/g, '_')}.png`;
            link.href = canvas.toDataURL();
            link.click();
          }
        }}
        onDownloadCSV={() => {
          // This would need to be implemented based on the chart data
          const viewLabel = evAdoptionView === 'total' ? 'Total' : evAdoptionView === 'perCapita' ? 'Per_Capita' : 'Projection';
          console.log(`CSV download for EV Adoption ${viewLabel} not yet implemented`);
        }}
        pngFilename={`EV_Adoption_${evAdoptionView === 'total' ? 'Total' : evAdoptionView === 'perCapita' ? 'Per_Capita' : 'Projection'}_${currentLocation.replace(/\s+/g, '_')}`}
        csvFilename={`EV_Adoption_${evAdoptionView === 'total' ? 'Total' : evAdoptionView === 'perCapita' ? 'Per_Capita' : 'Projection'}_Data_${currentLocation.replace(/\s+/g, '_')}`}
      />

      

      {/* EV Charging Infrastructure Section */}
      <ChartLayout
        title="EV Charging Infrastructure"
        description="Current charging station infrastructure compared to 2030 targets and accessibility metrics."
        chartType="bar"
        chartContent={
          <div id="ev-charging-chart">
            <EVChargingInfrastructure location={currentLocation} activeTab={evChargingView} onTabChange={setEvChargingView} />
          </div>
        }
        chartSelector={{
          options: [
            { value: 'counts', label: 'Total Chargers' },
            { value: 'peoplePerCharger', label: 'People per Charger' },
            { value: 'evsPerCharger', label: 'EVs per Charger' }
          ],
          value: evChargingView,
          onChange: (value: string) => setEvChargingView(value as 'counts' | 'peoplePerCharger' | 'evsPerCharger')
        }}
        activeView={activeChargingView}
        onViewChange={setActiveChargingView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        layout="side-by-side"
        onDownloadPNG={() => {
          const canvas = document.querySelector('#ev-charging-chart canvas') as HTMLCanvasElement;
          if (canvas) {
            const link = document.createElement('a');
            const viewLabel = evChargingView === 'counts' ? 'Total_Chargers' : evChargingView === 'peoplePerCharger' ? 'People_Per_Charger' : 'EVs_Per_Charger';
            link.download = `EV_Charging_${viewLabel}_${currentLocation.replace(/\s+/g, '_')}.png`;
            link.href = canvas.toDataURL();
            link.click();
          }
        }}
        onDownloadCSV={() => {
          // This would need to be implemented based on the chart data
          const viewLabel = evChargingView === 'counts' ? 'Total_Chargers' : evChargingView === 'peoplePerCharger' ? 'People_Per_Charger' : 'EVs_Per_Charger';
          console.log(`CSV download for EV Charging ${viewLabel} not yet implemented`);
        }}
        pngFilename={`EV_Charging_${evChargingView === 'counts' ? 'Total_Chargers' : evChargingView === 'peoplePerCharger' ? 'People_Per_Charger' : 'EVs_Per_Charger'}_${currentLocation.replace(/\s+/g, '_')}`}
        csvFilename={`EV_Charging_${evChargingView === 'counts' ? 'Total_Chargers' : evChargingView === 'peoplePerCharger' ? 'People_Per_Charger' : 'EVs_Per_Charger'}_Data_${currentLocation.replace(/\s+/g, '_')}`}
        overviewContent={
          <OverviewContent
            title="EV Charging Infrastructure Overview"
            description="This chart displays electric vehicle charging infrastructure data, comparing current capacity to 2030 targets."
          >
            <p>
              Electric vehicle charging infrastructure is a critical component of California's clean transportation transition. 
              This analysis examines the current state of EV charging infrastructure in {currentLocation} and compares it to 
              statewide targets and benchmarks.
            </p>
            <p>
              The data shows current charging station counts, including Level 2 chargers for daily charging and DC Fast 
              chargers for long-distance travel. We also analyze charging infrastructure density through metrics like 
              people per charger and EVs per charger to understand accessibility and utilization.
            </p>
            <p>
              California has set ambitious goals for EV adoption, with targets to reach 1 million charging stations by 2030. 
              Understanding current infrastructure gaps and planning for equitable distribution is essential for achieving 
              these goals while ensuring all communities benefit from the clean transportation transition.
            </p>
          </OverviewContent>
        }
        relevanceContent={
          <RelevanceContent
            title="Policy Relevance"
            description="Why EV charging infrastructure matters for climate action and local planning."
          >
            <p>
              <strong>Policy Relevance:</strong> EV charging infrastructure is directly tied to California's climate goals 
              and transportation electrification policies. The state's commitment to reach carbon neutrality by 2045 
              requires a massive expansion of clean transportation infrastructure.
            </p>
            <p>
              <strong>Local Planning:</strong> Cities and counties need to understand their current charging infrastructure 
              status to plan for future growth, allocate resources effectively, and ensure equitable access to clean 
              transportation options for all residents.
            </p>
            <p>
              <strong>Economic Development:</strong> Charging infrastructure supports local economic development by 
              attracting EV drivers, supporting local businesses, and creating jobs in the clean energy sector.
            </p>
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description="Ensuring equitable access to EV charging infrastructure across all communities."
          >
            <p>
              <strong>Accessibility Concerns:</strong> EV charging infrastructure must be distributed equitably to ensure 
              all communities can participate in the clean transportation transition. Historically underserved communities 
              may face barriers to EV adoption without adequate charging infrastructure.
            </p>
            <p>
              <strong>Multi-Family Housing:</strong> Residents of apartments and condominiums often lack access to home 
              charging, making public charging infrastructure even more critical for these communities.
            </p>
            <p>
              <strong>Rural and Underserved Areas:</strong> Ensuring charging infrastructure reaches rural communities and 
              areas with limited transportation options is essential for statewide equity in clean transportation access.
            </p>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Data Sources & Methodology"
            description="Detailed information about data sources and calculation methods for EV charging infrastructure analysis."
            dataSources={[
              'U.S. Department of Energy, Alternative Fuels Data Center (AFDC) Station Locator',
              'U.S. Census Bureau American Community Survey (ACS) 5-Year Estimates for 2023',
              'California Air Resources Board (CARB) EMFAC2021 model and CARB Fleet Database for 2023'
            ]}
            methodology={[
              'Charging station counts are aggregated by geographic location from AFDC database',
              'Data includes public and private charging stations with status: Available (E), Planned (P), and Temporarily Unavailable (T)',
              'Per capita calculations use 2023 population estimates from ACS',
              'EV counts include both Battery Electric Vehicles (BEVs) and Plug-in Hybrid Electric Vehicles (PHEVs)',
              'County-level data is used for comparison when city-level data is not available',
              '2030 targets based on California\'s goal to reach 1 million charging stations statewide'
            ]}
          />
        }
      />

      {/* EV Adoption Barriers by Income Quintile */}
      <EVAdoptionBarriersChart location={currentLocation} />

      {/* Means of Transportation to Work */}
      <MeansOfTransportation 
        location={currentLocation}
        geofips={selectedGeofips}
      />

    </div>
  );
};
